import styles from './V2OnboardingAgent.module.css';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config, { logger } from '../../config';
import { updateV2OnboardingAiResult } from '../../utils/api';
import { collegeYearRowLabel, financialProfileQuestionLabels, useContentCatalog } from '../../utils/contentCatalog';
import { formatCompactCurrencyTwoDecimals, getFinancialDashboardData } from '../../utils/onboardingUtils';

/** Strip to digits only (handles "$6,000", "6,000", stored numbers). */
export function parseCurrencyDigits(value) {
  if (value === undefined || value === null) return '';
  return String(value).replace(/\D/g, '');
}

/** US-style display: "$ 10,000" (space after $). */
export function formatUsdWithSpace(digitString) {
  if (digitString === undefined || digitString === null || digitString === '') return '$ ';
  const n = parseInt(String(digitString), 10);
  if (Number.isNaN(n)) return '$ ';
  return `$ ${n.toLocaleString('en-US')}`;
}

/** Count digits before the caret in a formatted currency string. */
function countDigitsBeforeCaret(formatted, caretIndex) {
  let count = 0;
  const end = Math.min(Math.max(0, caretIndex), formatted.length);
  for (let i = 0; i < end; i += 1) {
    if (/\d/.test(formatted[i])) count += 1;
  }
  return count;
}

/** Map a digit index in the raw value to a caret index in {@link formatUsdWithSpace} output. */
function caretIndexForDigitPosition(formatted, digitPosition) {
  const target = Math.max(0, digitPosition);
  let digitsSeen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      if (digitsSeen === target) return i;
      digitsSeen += 1;
    }
  }
  return formatted.length;
}

/**
 * Digit caret index after a currency edit, using the pre-edit selection (from `beforeinput` / keydown).
 * Avoids React controlled inputs reporting `selectionStart` at the end after reformat on delete.
 */
function computeDigitCaretAfterCurrencyInput({
  prevFormatted,
  prevSelStart = 0,
  prevSelEnd = prevSelStart,
  prevDigitLen,
  nextDigitLen,
  inputType,
}) {
  const digitsBeforeStart = countDigitsBeforeCaret(prevFormatted, prevSelStart);
  if (nextDigitLen < prevDigitLen) {
    if (prevSelEnd > prevSelStart) return digitsBeforeStart;
    if (inputType === 'deleteContentBackward' || inputType === 'Backspace') {
      return Math.max(0, digitsBeforeStart - 1);
    }
    return digitsBeforeStart;
  }
  if (nextDigitLen > prevDigitLen) {
    return digitsBeforeStart + (nextDigitLen - prevDigitLen);
  }
  return digitsBeforeStart;
}

function restoreTextInputSelection(input, selection) {
  if (!input || !selection) return;
  const len = input.value?.length ?? 0;
  const start = Math.min(selection.start ?? len, len);
  const end = Math.min(selection.end ?? start, len);
  try {
    input.setSelectionRange(start, end);
  } catch (_) {
    /* ignore */
  }
}

/**
 * Display defaults when chat has not set a field yet (must match `V2_FINANCIAL_PROFILE_DEFAULTS` in richuel-backend).
 * - Chat-captured / needs-input money: '' until filled; 0 and $0 are valid answers.
 * - Card 5 debts: default '0' (blank treated as $0 on backend).
 * - Sliders / annual contribution: AI-style defaults (annual contribution defaults to 0).
 */
export const FINANCIAL_PROFILE_DEFAULTS = {
  'welcome_message_answer': '',
  'duration': '',
  'monthly_net_income_during_career_shift': '',
  'monthly_net_income_after_career_shift': '',
  'family_situation': '',
  // Captured via proto3 chat (`/api/v2/onboarding-ai/proto3-turn`); optional geolocation can augment later.
  'your_year_of_birth': '',
  'partner_year_of_birth_of_birth': '',
  'monthly_net_income_partner': '',
  'children_number': '',
  'housing_situation': '',
  'retirement_age': '65',
  'partner_retire': '65',
  'your_social_security_benefits_age': '67',
  'partner_social_security_benefits_age': '67',
  'pension_plan': 'No',
  'pension_income_start_age': '',
  'pension_monthly_income': '',
  'annuity_plan': 'No',
  'annuity_income_start_age': '',
  'annuity_monthly_income': '',
  // Captured via proto3 chat; optional geolocation can augment later.
  'state': '',
  'city': '',
  'children_college_savings': '',
  'children_college_years': [],
  'total_retirement_account_each_year': '0',
  'monthly_expenses': '',
  'retirement_accounts': '',
  'total_balance_post_tax_retirement_accounts': '',
  'total_balance_equity_compensation_accounts': '',
  'other_investments': '',
  'home_estimated_value': '',
  'current_market_value': '',
  'home_mortgage_debt': '',
  'car_loan_debt': '0',
  'educational_loan': '0',
  'owe_credit_cards': '0',
  'other_debts_loans': '0',
};

/** Idle delay before autosave after profile edits (keystrokes reset the timer). */
const FINANCIAL_PROFILE_AUTOSAVE_DEBOUNCE_MS = 3000;

/** How long Reese auto-filled fields stay highlighted in the profile panel. */
const FINANCIAL_PROFILE_AUTOFILL_HIGHLIGHT_MS = 7000;

/**
 * Same merge as the panel’s debounced save: every key in {@link FINANCIAL_PROFILE_DEFAULTS},
 * with missing/empty values filled from defaults (matches backend `V2_FINANCIAL_PROFILE_DEFAULTS`).
 */
function buildReadableFinancialProfileFromAnswers(answers) {
  const defaults = FINANCIAL_PROFILE_DEFAULTS;
  const out = {};
  for (const key of Object.keys(defaults)) {
    const raw = answers?.[key];
    let hasValue = raw !== undefined && raw !== null;
    if (hasValue && Array.isArray(raw)) {
      hasValue = raw.length > 0;
    } else if (hasValue) {
      hasValue = String(raw).trim() !== '';
    }
    out[key] = hasValue ? raw : defaults[key];
  }
  return out;
}

/**
 * Section titles come from GET /api/content (`financial_profile_sections`).
 * Question ids below stay here because they are the form field keys.
 * Order matches that catalog: index 0 is the first section.
 */
const FINANCIAL_PROFILE_PANEL_QUESTION_IDS = [
  [
    'welcome_message_answer',
    'city',
    'state',
    'family_situation',
    'your_year_of_birth',
    'partner_year_of_birth_of_birth',
    'children_number',
    'housing_situation',
    'duration',
  ],
  [
    'monthly_net_income_during_career_shift',
    'monthly_net_income_after_career_shift',
    'monthly_net_income_partner',
    'monthly_expenses',
  ],
  [
    'retirement_accounts',
    'other_investments',
    'total_balance_post_tax_retirement_accounts',
    'total_balance_equity_compensation_accounts',
    'total_retirement_account_each_year',
  ],
  ['home_estimated_value', 'home_mortgage_debt', 'current_market_value'],
  [
    'car_loan_debt',
    'educational_loan',
    'owe_credit_cards',
    'other_debts_loans',
  ],
  [
    'retirement_age',
    'partner_retire',
    'your_social_security_benefits_age',
    'partner_social_security_benefits_age',
    'pension_plan',
    'pension_income_start_age',
    'pension_monthly_income',
    'annuity_plan',
    'annuity_income_start_age',
    'annuity_monthly_income',
  ],
  ['children_college_savings', 'children_college_years'],
];

/** 0-based index into the content-catalog financial profile sections per known question id */
export const FINANCIAL_PROFILE_QUESTION_PANEL_INDEX = Object.fromEntries(
  FINANCIAL_PROFILE_PANEL_QUESTION_IDS.flatMap((ids, panelIndex) =>
    ids.map((id) => [id, panelIndex])
  )
);

/**
 * Cross-panel sequence (0, 1, 2, …) for each known financial profile question id
 * (panel order in {@link FINANCIAL_PROFILE_PANEL_QUESTION_IDS}, top-to-bottom, panel 1→7).
 * Unknown ids are not listed; callers sort those after known ids (e.g. by string).
 */
export const FINANCIAL_PROFILE_QUESTION_GLOBAL_ORDER = (() => {
  const o = {};
  let n = 0;
  for (const ids of FINANCIAL_PROFILE_PANEL_QUESTION_IDS) {
    for (const id of ids) {
      if (o[id] === undefined) o[id] = n++;
    }
  }
  return o;
})();

/** Label for each child’s college year row. Copy comes from the content catalog. */
export function getFinancialProfileCollegeYearRowLabel(childIndexZeroBased) {
  return collegeYearRowLabel(childIndexZeroBased);
}

/**
 * IDs rendered as currency, slider age, or digit-only numeric inputs in this panel
 * (`CurrencyProfileRow`, `SliderAgeRow`, children count `DigitsOnlyRow`).
 * UserProfile uses this for synthetic question `type: 'number'` when there is no question metadata.
 * Excludes: text `ProfileRow` fields, birth years (handled as `year` in UserProfile), college array, toggles, state/city.
 */
export const FINANCIAL_PROFILE_NUMERIC_INPUT_IDS = new Set([
  'monthly_net_income_during_career_shift',
  'monthly_net_income_after_career_shift',
  'monthly_net_income_partner',
  'children_number',
  'children_college_savings',
  'retirement_age',
  'partner_retire',
  'your_social_security_benefits_age',
  'partner_social_security_benefits_age',
  'pension_income_start_age',
  'pension_monthly_income',
  'annuity_income_start_age',
  'annuity_monthly_income',
  'total_retirement_account_each_year',
  'monthly_expenses',
  'retirement_accounts',
  'total_balance_post_tax_retirement_accounts',
  'total_balance_equity_compensation_accounts',
  'other_investments',
  'home_estimated_value',
  'current_market_value',
  'home_mortgage_debt',
  'car_loan_debt',
  'educational_loan',
  'owe_credit_cards',
  'other_debts_loans',
]);

/** Slider age fields (30–95) — same as {@link SliderAgeRow} in this file. */
export const FINANCIAL_PROFILE_SLIDER_AGE_IDS = new Set([
  'retirement_age',
  'partner_retire',
  'your_social_security_benefits_age',
  'partner_social_security_benefits_age',
  'pension_income_start_age',
  'annuity_income_start_age',
]);

/** Money fields using {@link CurrencyProfileRow} / `$ X,XXX` display. */
export const FINANCIAL_PROFILE_CURRENCY_INPUT_IDS = new Set(
  [...FINANCIAL_PROFILE_NUMERIC_INPUT_IDS].filter(
    (id) => id !== 'children_number' && !FINANCIAL_PROFILE_SLIDER_AGE_IDS.has(id)
  )
);

/** Birth years use digit-only storage like other numeric fields in UserProfile. */
const FINANCIAL_PROFILE_DIGIT_ONLY_EXTRA_IDS = new Set([
  'your_year_of_birth',
  'partner_year_of_birth_of_birth',
]);

/**
 * Normalize raw answers (e.g. from UserProfile edits) to the same shape/encoding as the panel
 * before {@link buildReadableFinancialProfileFromAnswers}: college-year padding, digit-only money/ages.
 */
export function mergeFinancialProfileForPersist(answers) {
  if (!answers || typeof answers !== 'object') {
    return buildReadableFinancialProfileFromAnswers({});
  }
  const a = { ...answers };
  const count = parseChildrenCount(a['children_number']);
  if (count === 0) {
    a['children_college_years'] = [];
  } else {
    const arr = normalizeCollegeYearsArray(a['children_college_years']);
    const padded = [...arr];
    while (padded.length < count) padded.push('');
    a['children_college_years'] = padded.slice(0, count);
  }

  const digitIds = new Set([...FINANCIAL_PROFILE_NUMERIC_INPUT_IDS, ...FINANCIAL_PROFILE_DIGIT_ONLY_EXTRA_IDS]);
  for (const id of digitIds) {
    if (!Object.prototype.hasOwnProperty.call(a, id)) continue;
    const v = a[id];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s === '') {
      a[id] = '';
      continue;
    }
    const digits = parseCurrencyDigits(s);
    if (digits === '') {
      a[id] = '';
      continue;
    }
    const n = parseInt(digits, 10);
    a[id] = Number.isNaN(n) ? '' : String(n);
  }
  return buildReadableFinancialProfileFromAnswers(a);
}

/** Merge stored API profile with live panel answers (answers win) before normalize/persist. */
export function mergeRawProfileWithUserAnswersForPersist(financialProfileRaw, userAnswers) {
  const base =
    financialProfileRaw != null &&
    typeof financialProfileRaw === 'object' &&
    !Array.isArray(financialProfileRaw)
      ? { ...financialProfileRaw }
      : {};
  const answers = userAnswers != null && typeof userAnswers === 'object' && !Array.isArray(userAnswers) ? userAnswers : {};
  return mergeFinancialProfileForPersist({ ...base, ...answers });
}

export function parseChildrenCount(raw) {
  if (raw === undefined || raw === null) return 0;
  const s = String(raw).trim();
  if (!s) return 0;
  const direct = parseInt(s, 10);
  if (!Number.isNaN(direct) && s === String(direct)) return Math.min(5, Math.max(0, direct));
  const m = s.match(/(\d+)/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (!Number.isNaN(n)) return Math.min(5, Math.max(0, n));
  }
  return 0;
}

export function normalizeCollegeYearsArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    // Preserve null slots so index lines up with child N (backend may send ["2030", null, "not going"]).
    return raw.map((x) => {
      if (x === null || x === undefined) return null;
      const s = String(x);
      return s;
    });
  }
  if (typeof raw === 'string' && raw.trim()) {
    // Backend may send a JSON array string.
    try {
      const j = JSON.parse(raw);
      if (Array.isArray(j)) return normalizeCollegeYearsArray(j);
    } catch (_) {
      /* ignore */
    }
  }
  return [];
}

function fpIsPlainObject(x) {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

/** Root payload from GET /api/v2/onboarding-ai (handles `{ success, data }` or bare). */
export function v2OnboardingResponseRoot(apiResponse) {
  return apiResponse?.data ?? apiResponse;
}

export function getV2OnboardingAiResultFromResponse(apiResponse) {
  const d = v2OnboardingResponseRoot(apiResponse);
  return d?.v2_onboarding_ai_result ?? null;
}

/** Normalize financial_profile patch keys for panel state (e.g. college years array). */
export function normalizeFinancialProfilePatch(obj) {
  if (!fpIsPlainObject(obj)) return {};
  const out = { ...obj };
  if (Object.hasOwn(out, 'children_college_years')) {
    out['children_college_years'] = normalizeCollegeYearsArray(out['children_college_years']);
  }
  return out;
}

/** Pick known financial profile fields from `v2_onboarding_ai_result` for the panel. */
export function pickFinancialProfileFromV2Result(v2Result) {
  if (!fpIsPlainObject(v2Result)) return null;
  const candidate = fpIsPlainObject(v2Result.financial_profile)
    ? v2Result.financial_profile
    : v2Result;
  if (!fpIsPlainObject(candidate)) return null;
  const out = {};
  for (const k of Object.keys(FINANCIAL_PROFILE_DEFAULTS)) {
    if (candidate[k] !== undefined) out[k] = candidate[k];
  }
  return normalizeFinancialProfilePatch(out);
}

function hasWord(value, word) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  const escaped = String(word).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(s);
}

/** True when a stored/live answer is non-empty (arrays need at least one non-blank slot). */
function hasAnsweredValue(value) {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) {
    return value.some((x) => String(x ?? '').trim() !== '');
  }
  return String(value).trim() !== '';
}

/** Loose equality for highlight diffing (handles strings + nullable arrays). */
function fpValuesEqual(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i += 1) {
      if (String(aa[i] ?? '') !== String(bb[i] ?? '')) return false;
    }
    return true;
  }
  return String(a ?? '') === String(b ?? '');
}

const FORECAST_CHART = {
  width: 620,
  height: 280,
  padLeft: 44,
  padRight: 14,
  padTop: 10,
  padBottom: 26,
};

function formatCompactCurrency(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function formatAxisCurrency(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

function buildMiniChartPaths(points) {
  const { width, height, padLeft, padRight, padTop, padBottom } = FORECAST_CHART;
  const safePoints = Array.isArray(points) ? points.filter((p) => Number.isFinite(p?.value)) : [];
  const axisY = height - padBottom;
  const axisX2 = width - padRight;
  if (safePoints.length < 2) {
    return { linePath: '', areaPath: '', yTicks: [{ value: 0, y: padTop }], xTicks: [], axisY, axisX2 };
  }

  const innerW = Math.max(1, width - padLeft - padRight);
  const innerH = Math.max(1, height - padTop - padBottom);
  const values = safePoints.map((p) => p.value);
  const minValue = Math.min(0, ...values);
  const maxValue = Math.max(...values, 1);
  const span = Math.max(1, maxValue - minValue);

  const x = (i) => padLeft + (i / (safePoints.length - 1)) * innerW;
  const y = (v) => padTop + ((maxValue - v) / span) * innerH;
  const baselineY = y(0);

  const linePath = safePoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(p.value).toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L ${x(safePoints.length - 1).toFixed(2)} ${baselineY.toFixed(2)} L ${x(0).toFixed(2)} ${baselineY.toFixed(2)} Z`;

  const yTicks = Array.from({ length: 4 }, (_, i) => {
    const value = minValue + ((maxValue - minValue) * i) / 3;
    return { value, y: y(value) };
  });
  const last = safePoints.length - 1;
  const xTickIndices = [0, Math.floor(last * 0.25), Math.floor(last * 0.5), Math.floor(last * 0.75), last];
  const xTicks = Array.from(new Set(xTickIndices))
    .map((idx) => ({ idx, x: x(idx), label: safePoints[idx]?.label ?? '' }))
    .filter((t) => t.label);

  return { linePath, areaPath, yTicks, xTicks, axisY, axisX2 };
}

const ANTI_AUTOFILL_INPUT_PROPS = {
  autoCorrect: 'off',
  spellCheck: false,
  enterKeyHint: 'done',
  'data-form-type': 'other',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
};

const SLIDER_AGE_RANGE = { min: 30, max: 95, step: 1 };

const ZILLOW_LINK = (
  <a
    className={styles.inlineLink}
    href="https://www.zillow.com"
    target="_blank"
    rel="noopener noreferrer"
  >
    Check on Zillow
  </a>
);

function FieldLabel({ label, hint, hintTitle, labelAction, labelFooter }) {
  if (!hint && !labelAction && !labelFooter) {
    return <span className={styles.label}>{label}</span>;
  }
  return (
    <div className={styles.labelStack}>
      {labelAction ? (
        <div className={styles.labelInlineRow}>
          <span className={styles.label}>{label}</span>
          <span className={styles.labelInlineAction}>{labelAction}</span>
        </div>
      ) : (
        <span className={styles.label}>{label}</span>
      )}
      {hint ? (
        <span className={styles.fieldHint} title={hintTitle || undefined}>
          {hint}
        </span>
      ) : null}
      {labelFooter}
    </div>
  );
}

function AccordionRow({ label, hint, hintTitle, labelAction, labelFooter, showCheck, children }) {
  return (
    <div className={styles.rowAccordion}>
      <FieldLabel
        label={label}
        hint={hint}
        hintTitle={hintTitle}
        labelAction={labelAction}
        labelFooter={labelFooter}
      />
      {typeof showCheck === 'boolean' ? (
        <div className={styles.fpInputAffix}>
          {children}
          {showCheck ? <FpFilledCheckIcon /> : null}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function FpFilledCheckIcon() {
  return (
    <span className={styles.fpFilledCheck} aria-hidden>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path
          d="M20 6L9 17l-5-5"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function displayOrFallback(userAnswers, questionId, fallback) {
  const raw = userAnswers[questionId];
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') return String(raw);
  return String(fallback ?? '');
}

function fieldInputClass(isAutofilled, extraClass) {
  return `${styles.input}${extraClass ? ` ${extraClass}` : ''}${isAutofilled ? ` ${styles.inputAutofilled}` : ''}`;
}

function DigitsOnlyRow({
  label,
  questionId,
  userAnswers,
  setUserAnswers,
  defaults,
  maxLen,
  min,
  max,
  onUserEdit,
  onFieldCommit,
  isAutofilled = false,
  autoComplete = 'off',
}) {
  const inputRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const display = displayOrFallback(userAnswers, questionId, defaults[questionId] ?? '');

  useLayoutEffect(() => {
    restoreTextInputSelection(inputRef.current, pendingSelectionRef.current);
    pendingSelectionRef.current = null;
  }, [display]);

  const handleChange = (e) => {
    pendingSelectionRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };
    onUserEdit?.();
    let digits = e.target.value.replace(/\D/g, '');
    if (typeof maxLen === 'number' && maxLen > 0) digits = digits.slice(0, maxLen);
    if (digits === '') {
      setUserAnswers((prev) => ({ ...prev, [questionId]: '' }));
      return;
    }
    let n = parseInt(digits, 10);
    if (Number.isNaN(n)) n = 0;
    if (typeof min === 'number') n = Math.max(min, n);
    if (typeof max === 'number') n = Math.min(max, n);
    const nextStr = String(n);
    setUserAnswers((prev) => ({ ...prev, [questionId]: nextStr }));
    if (typeof maxLen === 'number' && maxLen > 0 && nextStr.length >= maxLen) {
      setTimeout(() => onFieldCommit?.(), 0);
    }
  };

  return (
    <AccordionRow label={label} showCheck={String(display).trim() !== ''}>
      <input
        ref={inputRef}
        className={fieldInputClass(isAutofilled)}
        type="text"
        name={`fp-${questionId}`}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete={autoComplete}
        {...ANTI_AUTOFILL_INPUT_PROPS}
        value={display}
        onChange={handleChange}
        onBlur={onFieldCommit}
        aria-label={label}
      />
    </AccordionRow>
  );
}

function ProfileRow({
  label,
  questionId,
  userAnswers,
  setUserAnswers,
  defaults,
  onUserEdit,
  onFieldCommit,
  isAutofilled = false,
  autoComplete = 'off',
  autoCapitalize = 'sentences',
}) {
  const inputRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const display = displayOrFallback(userAnswers, questionId, defaults[questionId] ?? '');

  useLayoutEffect(() => {
    restoreTextInputSelection(inputRef.current, pendingSelectionRef.current);
    pendingSelectionRef.current = null;
  }, [display]);

  const handleChange = (e) => {
    pendingSelectionRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };
    onUserEdit?.();
    setUserAnswers((prev) => ({ ...prev, [questionId]: e.target.value }));
  };

  return (
    <AccordionRow label={label} showCheck={String(display).trim() !== ''}>
      <input
        ref={inputRef}
        className={fieldInputClass(isAutofilled)}
        type="text"
        name={`fp-${questionId}`}
        inputMode="text"
        autoComplete={autoComplete}
        autoCorrect="on"
        autoCapitalize={autoCapitalize}
        spellCheck
        enterKeyHint="done"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        value={display}
        onChange={handleChange}
        onBlur={onFieldCommit}
        aria-label={label}
      />
    </AccordionRow>
  );
}

function SliderAgeRow({
  label,
  questionId,
  userAnswers,
  setUserAnswers,
  defaults,
  min = SLIDER_AGE_RANGE.min,
  max = SLIDER_AGE_RANGE.max,
  step = SLIDER_AGE_RANGE.step,
  onUserEdit,
  onFieldCommit,
}) {
  const fb = parseInt(String(defaults[questionId] ?? '65'), 10);
  const fallback = Number.isNaN(fb) ? 65 : fb;
  const parsed = parseInt(displayOrFallback(userAnswers, questionId, fallback), 10);
  const val = Number.isNaN(parsed) ? fallback : Math.min(max, Math.max(min, parsed));

  return (
    <AccordionRow label={label}>
      <div className={styles.sliderCell}>
        <input
          type="range"
          className={styles.sliderInput}
          min={min}
          max={max}
          step={step}
          value={val}
          onChange={(e) => {
            onUserEdit?.();
            setUserAnswers((prev) => ({ ...prev, [questionId]: e.target.value }));
          }}
          onMouseUp={onFieldCommit}
          onTouchEnd={onFieldCommit}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={val}
          aria-label={label}
        />
        <span className={styles.sliderValue} aria-hidden>
          {val}
        </span>
      </div>
    </AccordionRow>
  );
}

function ToggleYesNoRow({
  label,
  questionId,
  userAnswers,
  setUserAnswers,
  defaults,
  hint,
  hintTitle,
  onUserEdit,
  onFieldCommit,
}) {
  const fallback = String(defaults?.[questionId] ?? 'No');
  const stored = displayOrFallback(userAnswers, questionId, fallback);
  const value = hasWord(stored, 'yes') ? 'Yes' : 'No';

  const set = (next) => {
    onUserEdit?.();
    setUserAnswers((prev) => ({ ...prev, [questionId]: next }));
    setTimeout(() => onFieldCommit?.(), 0);
  };

  return (
    <AccordionRow label={label} hint={hint} hintTitle={hintTitle}>
      <div className={styles.toggleGroup} role="group" aria-label={label}>
        {['Yes', 'No'].map((option) => (
          <button
            key={option}
            type="button"
            className={`${styles.toggleBtn} ${styles[`toggleBtn--${option.toLowerCase()}`]} ${value === option ? styles['toggleBtn--active'] : ''}`}
            onClick={(e) => {
              set(option);
              e.currentTarget.blur();
            }}
          >
            {option}
          </button>
        ))}
      </div>
    </AccordionRow>
  );
}

function CurrencyProfileRow({
  label,
  questionId,
  userAnswers,
  setUserAnswers,
  defaults,
  hint,
  labelAction,
  labelFooter,
  onUserEdit,
  onFieldCommit,
  isAutofilled = false,
}) {
  const inputRef = useRef(null);
  const pendingCaretDigitsRef = useRef(null);
  const inputSnapshotRef = useRef(null);
  const raw = userAnswers[questionId];
  const hasStored = raw !== undefined && raw !== null && String(raw).trim() !== '';
  const digitString = hasStored ? parseCurrencyDigits(raw) : parseCurrencyDigits(defaults[questionId] ?? '');
  const display = formatUsdWithSpace(digitString);

  useLayoutEffect(() => {
    if (pendingCaretDigitsRef.current == null) return;
    const el = inputRef.current;
    if (!el) {
      pendingCaretDigitsRef.current = null;
      return;
    }
    const pos = caretIndexForDigitPosition(display, pendingCaretDigitsRef.current);
    pendingCaretDigitsRef.current = null;
    try {
      el.setSelectionRange(pos, pos);
    } catch (_) {
      /* ignore */
    }
  }, [display]);

  const captureInputSnapshot = (target, inputType = null) => {
    inputSnapshotRef.current = {
      start: target.selectionStart,
      end: target.selectionEnd,
      value: target.value,
      inputType,
    };
  };

  const handleChange = (e) => {
    const snap = inputSnapshotRef.current;
    inputSnapshotRef.current = null;
    const prevFormatted = snap?.value ?? display;
    const prevDigitLen = parseCurrencyDigits(prevFormatted).length;
    const nextDigits = e.target.value.replace(/\D/g, '');
    pendingCaretDigitsRef.current = snap
      ? computeDigitCaretAfterCurrencyInput({
          prevFormatted,
          prevSelStart: snap.start,
          prevSelEnd: snap.end,
          prevDigitLen,
          nextDigitLen: nextDigits.length,
          inputType: snap.inputType,
        })
      : countDigitsBeforeCaret(e.target.value, e.target.selectionStart ?? 0);
    onUserEdit?.();
    if (nextDigits === '') {
      setUserAnswers((prev) => ({ ...prev, [questionId]: '' }));
      return;
    }
    const n = parseInt(nextDigits, 10);
    setUserAnswers((prev) => ({ ...prev, [questionId]: Number.isNaN(n) ? '' : String(n) }));
  };

  return (
    <AccordionRow
      label={label}
      hint={hint}
      labelAction={labelAction}
      labelFooter={labelFooter}
      showCheck={digitString !== '' && parseInt(digitString, 10) > 0}
    >
      <input
        ref={inputRef}
        className={fieldInputClass(isAutofilled, styles.inputCurrency)}
        type="text"
        name={`fp-${questionId}`}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        {...ANTI_AUTOFILL_INPUT_PROPS}
        value={display}
        onBeforeInput={(e) => captureInputSnapshot(e.target, e.inputType)}
        onKeyDown={(e) => {
          if (e.key === 'Backspace' || e.key === 'Delete') {
            captureInputSnapshot(
              e.target,
              e.key === 'Backspace' ? 'deleteContentBackward' : 'deleteContentForward'
            );
          }
        }}
        onChange={handleChange}
        onBlur={onFieldCommit}
        aria-label={label}
      />
    </AccordionRow>
  );
}

function CollegeYearRow({
  childIndex,
  label,
  userAnswers,
  setUserAnswers,
  childCount,
  onUserEdit,
  onFieldCommit,
  isAutofilled = false,
}) {
  const inputRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const y = normalizeCollegeYearsArray(userAnswers['children_college_years'])[childIndex];
  const display = y != null ? String(y) : '';

  useLayoutEffect(() => {
    restoreTextInputSelection(inputRef.current, pendingSelectionRef.current);
    pendingSelectionRef.current = null;
  }, [display]);

  const handleChange = (e) => {
    pendingSelectionRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };
    onUserEdit?.();
    const v = e.target.value;
    setUserAnswers((prev) => {
      const next = [...normalizeCollegeYearsArray(prev['children_college_years'])];
      next[childIndex] = v;
      const padded = [...next];
      while (padded.length < childCount) padded.push('');
      return { ...prev, children_college_years: padded.slice(0, childCount) };
    });
  };

  return (
    <AccordionRow label={label} showCheck={String(display).trim() !== ''}>
      <input
        ref={inputRef}
        className={fieldInputClass(isAutofilled)}
        type="text"
        name={`fp-college-year-${childIndex}`}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        {...ANTI_AUTOFILL_INPUT_PROPS}
        value={display}
        onChange={handleChange}
        onBlur={onFieldCommit}
        aria-label={label}
      />
    </AccordionRow>
  );
}

function ProfileAccordion({ sectionIndex, number, title, isOpen, onToggle, children }) {
  return (
    <section
      className={styles.fpAccordion}
      style={{ '--fp-section-index': sectionIndex }}
    >
      <button
        type="button"
        className={`${styles.fpAccordionHead} ${isOpen ? styles['fpAccordionHead--open'] : ''}`}
        aria-expanded={isOpen}
        aria-controls={`fp-acc-panel-${sectionIndex}`}
        id={`fp-acc-${sectionIndex}`}
        onClick={onToggle}
      >
        <span className={styles.fpAccordionBadge}>{number}</span>
        <span className={styles.fpAccordionTitle}>{title}</span>
        <span className={styles.fpAccordionChevron} aria-hidden>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      <div
        className={`${styles.fpAccordionBodyWrap} ${isOpen ? styles['fpAccordionBodyWrap--open'] : ''}`}
        id={`fp-acc-panel-${sectionIndex}`}
        role="region"
        aria-labelledby={`fp-acc-${sectionIndex}`}
        aria-hidden={!isOpen}
      >
        <div className={styles.fpAccordionBody}>
          {children}
        </div>
      </div>
    </section>
  );
}

function isPartneredFamily(value) {
  return hasWord(value, 'married') || hasWord(value, 'partnered');
}

function mapAgeSeries(rows, valueOf) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const startYear = new Date().getFullYear();
  const firstAge = Number(rows[0]?.age) || 0;
  return rows.map((r) => ({
    value: valueOf(r),
    label: String(startYear + Math.max(0, (Number(r.age) || firstAge) - firstAge)),
  }));
}

function cashflowPointValue(r) {
  const workIncome = Number(r.workIncome ?? r.income) || 0;
  return (
    workIncome +
    (Number(r.socialSecurity) || 0) +
    (Number(r.pension) || 0) +
    (Number(r.annuity) || 0) +
    (Number(r.rmd) || 0) +
    (Number(r.collegeSavingsDrawdown) || 0) +
    (Number(r.savingsDrawdown) || 0)
  );
}

function netWorthPointValue(r) {
  return Number.isFinite(Number(r.futureDollar)) ? Number(r.futureDollar) : Number(r.currentDollar) || 0;
}

function monthlyAmountFromAnswer(getEffectiveAnsweredValue, id) {
  return Number(parseCurrencyDigits(String(getEffectiveAnsweredValue(id) ?? ''))) || 0;
}

function ForecastMiniChart({ geometry, loading }) {
  const { width, height, padLeft } = FORECAST_CHART;
  return (
    <div className={styles.fpForecastChartWrap}>
      <svg
        className={styles.fpForecastChart}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="fpForecastArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(157, 218, 255, 0.45)" />
            <stop offset="100%" stopColor="rgba(30, 90, 130, 0.1)" />
          </linearGradient>
        </defs>
        {geometry.yTicks.map((tick, idx) => (
          <g key={`y-${idx}`}>
            <line
              x1={padLeft}
              y1={tick.y}
              x2={geometry.axisX2}
              y2={tick.y}
              className={styles.fpForecastGridLine}
            />
            <text x="2" y={tick.y + 4} className={styles.fpForecastAxisLabel}>
              {formatAxisCurrency(tick.value)}
            </text>
          </g>
        ))}
        {geometry.areaPath ? <path d={geometry.areaPath} className={styles.fpForecastArea} /> : null}
        {geometry.linePath ? <path d={geometry.linePath} className={styles.fpForecastLine} /> : null}
        <line
          x1={padLeft}
          y1={geometry.axisY}
          x2={geometry.axisX2}
          y2={geometry.axisY}
          className={styles.fpForecastAxisBase}
        />
        {geometry.xTicks.map((tick) => (
          <text
            key={`x-${tick.idx}`}
            x={tick.x}
            y={height - 6}
            textAnchor="middle"
            className={styles.fpForecastAxisLabel}
          >
            {tick.label}
          </text>
        ))}
      </svg>
      {loading ? <div className={styles.fpForecastLoading}>Loading forecast...</div> : null}
    </div>
  );
}

function ForecastMetric({ index, label, value, accent = false }) {
  return (
    <div style={{ '--fp-metric-index': index }}>
      <p className={styles.fpForecastMetricLabel}>{label}</p>
      <p className={accent ? styles.fpForecastMetricValueAccent : styles.fpForecastMetricValue}>{value}</p>
    </div>
  );
}

/**
 * Right-hand financial summary for V2 onboarding; field keys match backend question ids.
 *
 * @param {(data: object | null) => void} [onFinancialProfilePersisted] — called after a successful PUT to update-result with `response.data` (includes `onboarding_ai_done`, `completion_message` when the profile is complete).
 * @param {boolean} [showScenarioCta] — show the Copilot forecast card at the top of the panel.
 */
export default function FinancialProfilePanel({
  userAnswers,
  setUserAnswers,
  onFinancialProfilePersisted,
  showScenarioCta = true,
  onboardingAiDone = false,
  financialProfileRaw = null,
}) {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const sectionTitles = useMemo(
    () => (catalog?.financial_profile_sections || []).map((item) => item.title),
    [catalog],
  );
  const questionLabels = useMemo(
    () => financialProfileQuestionLabels(catalog?.financial_profile_fields?.fields),
    [catalog],
  );
  const [forecastTab, setForecastTab] = useState('cashflow');
  const [forecastData, setForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [runScenarioBusy, setRunScenarioBusy] = useState(false);
  const [isScenarioTooltipOpen, setIsScenarioTooltipOpen] = useState(false);
  const [openSections, setOpenSections] = useState(() => new Set([0, 1, 2, 3, 4, 5, 6]));
  const [autofilledIds, setAutofilledIds] = useState(() => new Set());
  const saveTimerRef = useRef(null);
  const forecastTimerRef = useRef(null);
  const forecastAbortRef = useRef(null);
  const runForecastFetchRef = useRef(async () => {});
  const canComputeForecastRef = useRef(false);
  const lastSavedJsonRef = useRef('');
  const inFlightJsonRef = useRef('');
  const hasUserEditedRef = useRef(false);
  const panelScrollRef = useRef(null);
  const userAnswersRef = useRef(userAnswers);
  userAnswersRef.current = userAnswers;
  const autofillTimersRef = useRef(new Map());
  const prevEffectiveValuesRef = useRef(null);
  const lastUserEditedIdRef = useRef({ id: null, ts: 0 });

  const markUserEdited = useCallback((questionId) => {
    hasUserEditedRef.current = true;
    if (questionId) lastUserEditedIdRef.current = { id: questionId, ts: Date.now() };
  }, []);

  const persistV2OnboardingAiResult = useCallback(async (financialProfile, options = {}) => {
    const markOnboardingAiDone = Boolean(options.markOnboardingAiDone);
    const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN) || '';
    if (!token) return false;
    const result = await updateV2OnboardingAiResult(
      {
        token,
        v2OnboardingAiResult: {
          financial_profile: financialProfile,
          updated_at: Date.now(),
        },
        onboardingAiDone: markOnboardingAiDone,
      },
      null
    );
    if (!result.ok) {
      logger.warn('Failed to save V2 financial profile:', result.message || 'Unknown error');
      return false;
    }
    onFinancialProfilePersisted?.(result.data ?? null);
    return true;
  }, [onFinancialProfilePersisted]);

  const runPersistSave = useCallback(async () => {
    if (!hasUserEditedRef.current) return;
    const financialProfile = mergeFinancialProfileForPersist(userAnswersRef.current);
    const json = JSON.stringify(financialProfile);
    if (json === lastSavedJsonRef.current || json === inFlightJsonRef.current) return;
    inFlightJsonRef.current = json;
    const ok = await persistV2OnboardingAiResult(financialProfile);
    if (ok) lastSavedJsonRef.current = json;
    if (inFlightJsonRef.current === json) inFlightJsonRef.current = '';
  }, [persistV2OnboardingAiResult]);

  const flushPersistSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (forecastTimerRef.current) {
      clearTimeout(forecastTimerRef.current);
      forecastTimerRef.current = null;
    }
    setTimeout(() => {
      void runPersistSave();
      void runForecastFetchRef.current();
    }, 0);
  }, [runPersistSave]);

  const toggleSection = useCallback((index) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  useEffect(() => {
    const root = panelScrollRef.current;
    if (!root) return undefined;

    let timerId = 0;
    const onFocusIn = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
      window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        try {
          target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        } catch {
          target.scrollIntoView(true);
        }
      }, 280);
    };

    root.addEventListener('focusin', onFocusIn);
    return () => {
      window.clearTimeout(timerId);
      root.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  useEffect(() => {
    if (!hasUserEditedRef.current) return;
    const financialProfile = mergeFinancialProfileForPersist(userAnswers);
    const json = JSON.stringify(financialProfile);
    if (json === lastSavedJsonRef.current || json === inFlightJsonRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void runPersistSave();
    }, FINANCIAL_PROFILE_AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [userAnswers, runPersistSave]);

  const getEffectiveAnsweredValue = useCallback(
    (id) => {
      const live = userAnswers?.[id];
      if (hasAnsweredValue(live)) return live;
      const raw = financialProfileRaw?.[id];
      if (hasAnsweredValue(raw)) return raw;
      return undefined;
    },
    [userAnswers, financialProfileRaw]
  );

  const hasAnsweredId = useCallback(
    (id) => hasAnsweredValue(getEffectiveAnsweredValue(id)),
    [getEffectiveAnsweredValue]
  );

  useEffect(() => {
    const ids = Object.keys(FINANCIAL_PROFILE_DEFAULTS);
    const current = {};
    ids.forEach((id) => {
      current[id] = getEffectiveAnsweredValue(id);
    });
    const prev = prevEffectiveValuesRef.current;
    prevEffectiveValuesRef.current = current;
    if (prev == null) return;

    const lastEdit = lastUserEditedIdRef.current;
    const newlyFilled = ids.filter((id) => {
      const after = current[id];
      if (!hasAnsweredValue(after) || fpValuesEqual(prev[id], after)) return false;
      return !(lastEdit?.id === id && Date.now() - lastEdit.ts < 1500);
    });
    if (!newlyFilled.length) return;

    setAutofilledIds((prevSet) => {
      const next = new Set(prevSet);
      newlyFilled.forEach((id) => next.add(id));
      return next;
    });
    const timers = autofillTimersRef.current;
    newlyFilled.forEach((id) => {
      if (timers.has(id)) clearTimeout(timers.get(id));
      const t = setTimeout(() => {
        setAutofilledIds((prevSet) => {
          const next = new Set(prevSet);
          next.delete(id);
          return next;
        });
        timers.delete(id);
      }, FINANCIAL_PROFILE_AUTOFILL_HIGHLIGHT_MS);
      timers.set(id, t);
    });
  }, [userAnswers, financialProfileRaw, getEffectiveAnsweredValue]);

  useEffect(() => {
    const timers = autofillTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const canRunScenario = useMemo(() => {
    if (
      !hasAnsweredId('your_year_of_birth') ||
      !hasAnsweredId('family_situation') ||
      !hasAnsweredId('monthly_net_income_during_career_shift') ||
      !hasAnsweredId('monthly_net_income_after_career_shift')
    ) {
      return false;
    }
    const showPartnerReq = isPartneredFamily(getEffectiveAnsweredValue('family_situation'));
    return !showPartnerReq || hasAnsweredId('monthly_net_income_partner');
  }, [hasAnsweredId, getEffectiveAnsweredValue]);

  const canComputeForecast = useMemo(() => {
    const hasIncome =
      hasAnsweredId('monthly_net_income_after_career_shift') ||
      hasAnsweredId('monthly_net_income_during_career_shift');
    const hasAnyStartingAssets =
      hasAnsweredId('retirement_accounts') ||
      hasAnsweredId('other_investments') ||
      hasAnsweredId('total_balance_post_tax_retirement_accounts') ||
      hasAnsweredId('total_balance_equity_compensation_accounts');
    if (
      !hasIncome ||
      !hasAnsweredId('monthly_expenses') ||
      !hasAnsweredId('retirement_age') ||
      !hasAnyStartingAssets
    ) {
      return false;
    }

    if (isPartneredFamily(getEffectiveAnsweredValue('family_situation'))) {
      const okPartner =
        hasAnsweredId('monthly_net_income_partner') &&
        hasAnsweredId('partner_retire') &&
        hasAnsweredId('partner_social_security_benefits_age');
      if (!okPartner) return false;
    }

    if (hasWord(getEffectiveAnsweredValue('housing_situation'), 'own')) {
      if (!hasAnsweredId('home_estimated_value') || !hasAnsweredId('home_mortgage_debt')) return false;
    }

    if (hasWord(getEffectiveAnsweredValue('pension_plan') ?? FINANCIAL_PROFILE_DEFAULTS.pension_plan, 'yes')) {
      if (!hasAnsweredId('pension_income_start_age') || !hasAnsweredId('pension_monthly_income')) return false;
    }
    if (hasWord(getEffectiveAnsweredValue('annuity_plan') ?? FINANCIAL_PROFILE_DEFAULTS.annuity_plan, 'yes')) {
      if (!hasAnsweredId('annuity_income_start_age') || !hasAnsweredId('annuity_monthly_income')) return false;
    }

    return true;
  }, [hasAnsweredId, getEffectiveAnsweredValue]);

  canComputeForecastRef.current = canComputeForecast;

  const runForecastFetch = useCallback(async () => {
    forecastAbortRef.current?.abort();
    const ac = new AbortController();
    forecastAbortRef.current = ac;

    if (!canComputeForecastRef.current) {
      setForecastData(null);
      setForecastLoading(false);
      return;
    }

    setForecastLoading(true);
    try {
      const financialProfile = mergeFinancialProfileForPersist(userAnswersRef.current);
      const dashboardData = await getFinancialDashboardData(financialProfile, ac.signal);
      if (!ac.signal.aborted) setForecastData(dashboardData);
    } catch (e) {
      if (e?.name !== 'AbortError') logger.error('Failed to load mini forecast data:', e);
    } finally {
      if (!ac.signal.aborted) setForecastLoading(false);
    }
  }, []);

  runForecastFetchRef.current = runForecastFetch;

  useEffect(() => {
    if (!canComputeForecast) {
      if (forecastTimerRef.current) {
        clearTimeout(forecastTimerRef.current);
        forecastTimerRef.current = null;
      }
      forecastAbortRef.current?.abort();
      setForecastData(null);
      setForecastLoading(false);
      return;
    }

    if (forecastTimerRef.current) clearTimeout(forecastTimerRef.current);
    forecastTimerRef.current = setTimeout(() => {
      forecastTimerRef.current = null;
      void runForecastFetch();
    }, FINANCIAL_PROFILE_AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (forecastTimerRef.current) {
        clearTimeout(forecastTimerRef.current);
        forecastTimerRef.current = null;
      }
      forecastAbortRef.current?.abort();
    };
  }, [userAnswers, financialProfileRaw, canComputeForecast, runForecastFetch]);

  const netWorthSeries = useMemo(
    () => mapAgeSeries(forecastData?.projectedNetWorth?.projectionData, netWorthPointValue),
    [forecastData]
  );
  const cashflowSeries = useMemo(
    () => mapAgeSeries(forecastData?.projectedCashFlow?.data, cashflowPointValue),
    [forecastData]
  );
  const activeSeries = forecastTab === 'cashflow' ? cashflowSeries : netWorthSeries;
  const chartGeometry = useMemo(() => buildMiniChartPaths(activeSeries), [activeSeries]);

  const showPartner = isPartneredFamily(userAnswers.family_situation);
  const showPartnerReq = isPartneredFamily(getEffectiveAnsweredValue('family_situation'));
  const showRealEstateCard = hasWord(userAnswers.housing_situation, 'own');
  const childCount = parseChildrenCount(userAnswers.children_number);
  const showCollegeCard = childCount > 0;
  const pensionYes = hasWord(userAnswers.pension_plan ?? FINANCIAL_PROFILE_DEFAULTS.pension_plan, 'yes');
  const annuityYes = hasWord(userAnswers.annuity_plan ?? FINANCIAL_PROFILE_DEFAULTS.annuity_plan, 'yes');

  const renderRow = (def, rowIndex = 0) => {
    const questionId = def.questionId;
    const isAutofilled = autofilledIds.has(questionId);
    const common = {
      userAnswers,
      setUserAnswers,
      defaults: FINANCIAL_PROFILE_DEFAULTS,
      onUserEdit: () => markUserEdited(questionId),
      onFieldCommit: flushPersistSave,
      isAutofilled,
    };
    const label = def.label ?? questionLabels[questionId];

    let rowEl = null;
    switch (def.kind) {
      case 'profile':
        rowEl = (
          <ProfileRow
            label={label}
            questionId={questionId}
            autoComplete={questionId === 'city' ? 'address-level2' : questionId === 'state' ? 'address-level1' : 'off'}
            autoCapitalize={questionId === 'state' || questionId === 'city' ? 'words' : 'sentences'}
            {...common}
          />
        );
        break;
      case 'digits':
        rowEl = (
          <DigitsOnlyRow
            label={label}
            questionId={questionId}
            maxLen={def.maxLen}
            min={def.min}
            max={def.max}
            autoComplete={
              questionId === 'your_year_of_birth' || questionId === 'partner_year_of_birth_of_birth'
                ? 'bday-year'
                : 'off'
            }
            {...common}
          />
        );
        break;
      case 'currency':
        rowEl = (
          <CurrencyProfileRow
            label={label}
            questionId={questionId}
            hint={def.hint}
            labelAction={def.labelAction}
            labelFooter={def.labelFooter}
            {...common}
          />
        );
        break;
      case 'sliderAge':
        rowEl = (
          <SliderAgeRow
            label={label}
            questionId={questionId}
            min={def.min}
            max={def.max}
            step={def.step}
            {...common}
          />
        );
        break;
      case 'toggleYesNo':
        rowEl = (
          <ToggleYesNoRow
            label={label}
            questionId={questionId}
            hint={def.hint}
            hintTitle={def.hintTitle}
            {...common}
          />
        );
        break;
      case 'collegeYear':
        rowEl = (
          <CollegeYearRow
            childIndex={def.childIndex}
            childCount={def.childCount}
            label={def.label}
            userAnswers={userAnswers}
            setUserAnswers={setUserAnswers}
            isAutofilled={autofilledIds.has('children_college_years')}
            onUserEdit={() => markUserEdited('children_college_years')}
            onFieldCommit={flushPersistSave}
          />
        );
        break;
      default:
        return null;
    }

    const wrapClass = `${styles.fpAutofillWrap}${isAutofilled ? ` ${styles['fpAutofillWrap--active']}` : ''}`;
    const inner = (
      <div
        key={def.key || questionId}
        className={wrapClass}
        style={{ '--fp-row-index': rowIndex }}
      >
        {rowEl}
      </div>
    );
    return def.fullWidth ? <div key={def.key || questionId} className={styles.fpGridFull}>{inner}</div> : inner;
  };

  const sectionRows = [
    [
      { kind: 'profile', questionId: 'welcome_message_answer', fullWidth: true },
      { kind: 'profile', questionId: 'city' },
      { kind: 'profile', questionId: 'state' },
      { kind: 'digits', questionId: 'your_year_of_birth', maxLen: 4, label: 'Year Of Birth' },
      { kind: 'profile', questionId: 'family_situation' },
      { kind: 'digits', questionId: 'children_number', maxLen: 1, min: 0, max: 5 },
      { kind: 'profile', questionId: 'housing_situation' },
      { kind: 'profile', questionId: 'duration' },
      ...(showPartner ? [{ kind: 'digits', questionId: 'partner_year_of_birth_of_birth', maxLen: 4 }] : []),
    ],
    [
      { kind: 'currency', questionId: 'monthly_net_income_during_career_shift' },
      { kind: 'currency', questionId: 'monthly_net_income_after_career_shift' },
      ...(showPartnerReq ? [{ kind: 'currency', questionId: 'monthly_net_income_partner' }] : []),
      { kind: 'currency', questionId: 'monthly_expenses' },
    ],
    [
      { kind: 'currency', questionId: 'retirement_accounts' },
      { kind: 'currency', questionId: 'other_investments' },
      { kind: 'currency', questionId: 'total_balance_post_tax_retirement_accounts' },
      { kind: 'currency', questionId: 'total_balance_equity_compensation_accounts' },
      { kind: 'currency', questionId: 'total_retirement_account_each_year', fullWidth: true },
    ],
    [
      { kind: 'currency', questionId: 'home_estimated_value', labelAction: ZILLOW_LINK },
      { kind: 'currency', questionId: 'home_mortgage_debt' },
      { kind: 'currency', questionId: 'current_market_value', fullWidth: true },
    ],
    [
      { kind: 'currency', questionId: 'car_loan_debt' },
      { kind: 'currency', questionId: 'educational_loan' },
      { kind: 'currency', questionId: 'owe_credit_cards' },
      { kind: 'currency', questionId: 'other_debts_loans' },
    ],
    [
      { kind: 'sliderAge', questionId: 'retirement_age', ...SLIDER_AGE_RANGE, fullWidth: true },
      ...(showPartner ? [{ kind: 'sliderAge', questionId: 'partner_retire', ...SLIDER_AGE_RANGE, fullWidth: true }] : []),
      { kind: 'sliderAge', questionId: 'your_social_security_benefits_age', ...SLIDER_AGE_RANGE, fullWidth: true },
      ...(showPartner
        ? [{ kind: 'sliderAge', questionId: 'partner_social_security_benefits_age', ...SLIDER_AGE_RANGE, fullWidth: true }]
        : []),
      { kind: 'toggleYesNo', questionId: 'pension_plan', fullWidth: true },
      ...(pensionYes
        ? [
            { kind: 'sliderAge', questionId: 'pension_income_start_age', ...SLIDER_AGE_RANGE, fullWidth: true },
            { kind: 'currency', questionId: 'pension_monthly_income', fullWidth: true },
          ]
        : []),
      { kind: 'toggleYesNo', questionId: 'annuity_plan', fullWidth: true },
      ...(annuityYes
        ? [
            { kind: 'sliderAge', questionId: 'annuity_income_start_age', ...SLIDER_AGE_RANGE, fullWidth: true },
            { kind: 'currency', questionId: 'annuity_monthly_income', fullWidth: true },
          ]
        : []),
    ],
    [
      { kind: 'currency', questionId: 'children_college_savings', fullWidth: true },
      ...Array.from({ length: childCount }, (_, i) => ({
        kind: 'collegeYear',
        questionId: 'children_college_years',
        key: `college-${i}`,
        childIndex: i,
        childCount,
        label: getFinancialProfileCollegeYearRowLabel(i),
        fullWidth: true,
      })),
    ],
  ];

  const visibleSectionIndices = [0, 1, 2, ...(showRealEstateCard ? [3] : []), 4, 5, ...(showCollegeCard ? [6] : [])];
  const sectionNumberByIndex = new Map(visibleSectionIndices.map((idx, order) => [idx, order + 1]));

  const currentAssetsValue = Number(forecastData?.currentNetWorth?.value) || 0;
  const projectedAtLongevityValue =
    Number(forecastData?.liquidNetWorthAtDeath ?? forecastData?.projectedNetWorth?.longevity) || 0;
  const partnerMonthlyForHousehold =
    showPartner && hasAnsweredId('monthly_net_income_partner')
      ? monthlyAmountFromAnswer(getEffectiveAnsweredValue, 'monthly_net_income_partner')
      : 0;
  const monthlyHouseholdCurrentValue =
    (hasAnsweredId('monthly_net_income_during_career_shift')
      ? monthlyAmountFromAnswer(getEffectiveAnsweredValue, 'monthly_net_income_during_career_shift')
      : 0) + partnerMonthlyForHousehold;
  const monthlyHouseholdPostShiftValue =
    (hasAnsweredId('monthly_net_income_after_career_shift')
      ? monthlyAmountFromAnswer(getEffectiveAnsweredValue, 'monthly_net_income_after_career_shift')
      : 0) + partnerMonthlyForHousehold;

  const runEnabled = canRunScenario || onboardingAiDone;
  const dash = '—';
  const forecastReady = canComputeForecast && forecastData;

  const handleRunScenario = async () => {
    if (!runEnabled || runScenarioBusy) return;
    setRunScenarioBusy(true);
    try {
      const financialProfile = mergeRawProfileWithUserAnswersForPersist(financialProfileRaw, userAnswers);
      await persistV2OnboardingAiResult(financialProfile, { markOnboardingAiDone: true });
    } finally {
      setRunScenarioBusy(false);
    }
    navigate('/career-shift-scenario-map');
  };

  return (
    <aside className={`${styles.panel} ${styles.panelFinancialDark}`} aria-label="Financial profile">
      <div
        ref={panelScrollRef}
        className={styles.panelScroll}
        role="region"
        aria-label="Financial profile fields"
      >
        {showScenarioCta ? (
          <section className={`${styles.fpForecastCard} ${styles.fpForecastCardReveal}`} aria-label="Projected forecast">
            <div className={styles.fpForecastHeader}>
              <div>
                <h3 className={styles.fpForecastTitle}>
                  Your projected{' '}
                  <span className={styles.fpForecastAccent}>
                    {forecastTab === 'cashflow' ? 'cashflow' : 'net worth'}
                  </span>{' '}
                  over time
                </h3>
                <p className={styles.fpForecastSubtitle}>
                  {canComputeForecast
                    ? 'Based on your profile inputs — update any field to refresh.'
                    : 'Fill key profile inputs to generate your forecast.'}
                </p>
              </div>
              <div className={styles.fpForecastTabs} role="tablist" aria-label="Forecast views">
                {[
                  { id: 'cashflow', label: 'Cashflow' },
                  { id: 'netWorth', label: 'Net Worth' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`${styles.fpForecastTab}${forecastTab === tab.id ? ` ${styles['fpForecastTab--active']}` : ''}`}
                    role="tab"
                    aria-selected={forecastTab === tab.id}
                    onClick={() => setForecastTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <ForecastMiniChart geometry={chartGeometry} loading={forecastLoading} />

            <div className={styles.fpForecastMetrics}>
              <ForecastMetric
                index={0}
                label="CURRENT ASSETS"
                value={forecastReady ? formatCompactCurrency(currentAssetsValue) : dash}
              />
              <ForecastMetric
                index={1}
                label="PEAK PROJECTED"
                value={forecastReady ? formatCompactCurrencyTwoDecimals(projectedAtLongevityValue) : dash}
                accent
              />
              <ForecastMetric
                index={2}
                label="MONTHLY HOUSEHOLD INCOME (CURRENT)"
                value={
                  hasAnsweredId('monthly_net_income_during_career_shift')
                    ? formatCompactCurrency(monthlyHouseholdCurrentValue)
                    : dash
                }
              />
              <ForecastMetric
                index={3}
                label="MONTHLY HOUSEHOLD INCOME (POST-SHIFT)"
                value={
                  hasAnsweredId('monthly_net_income_after_career_shift')
                    ? formatCompactCurrency(monthlyHouseholdPostShiftValue)
                    : dash
                }
              />
            </div>
            <div className={styles.fpForecastScenarioActions}>
              <div className={styles.fpForecastTooltipWrap}>
                <button
                  type="button"
                  className={styles.fpForecastTooltipTrigger}
                  aria-label="Forecast assumptions info"
                  aria-expanded={isScenarioTooltipOpen}
                  onClick={() => setIsScenarioTooltipOpen((open) => !open)}
                >
                  i
                </button>
                {isScenarioTooltipOpen ? (
                  <div className={styles.fpForecastTooltip} role="tooltip">
                    We&apos;ve applied sensible defaults to get your forecast started. Update your profile for higher
                    accuracy, and we&apos;ll guide you through the Scenario Map to finalize your results.
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.fpForecastRunButton}
                disabled={!runEnabled || runScenarioBusy}
                aria-disabled={!runEnabled || runScenarioBusy}
                onClick={handleRunScenario}
              >
                Run Scenario
              </button>
            </div>
          </section>
        ) : null}

        {visibleSectionIndices.map((sectionIndex) => (
          <ProfileAccordion
            key={sectionIndex}
            sectionIndex={sectionIndex}
            number={sectionNumberByIndex.get(sectionIndex)}
            title={sectionTitles[sectionIndex] || ''}
            isOpen={openSections.has(sectionIndex)}
            onToggle={() => toggleSection(sectionIndex)}
          >
            {sectionRows[sectionIndex].map((def, i) => renderRow(def, i))}
          </ProfileAccordion>
        ))}
      </div>
    </aside>
  );
}
